/**
 * sl-vw37.1.3: Vertical Gate Provider Factory
 *
 * Creates React provider components for vertical-specific body class gating.
 * Each provider reads plan + connectivity from FeatureGateContext and toggles
 * a body class so Lit components can check vertical availability.
 *
 * SOLID:
 * - S: Only creates gate provider components
 * - O: New verticals call this factory — no modification needed here
 */
import { type FC, type PropsWithChildren, useEffect } from 'react';

import { useFeatureGate } from '../hooks/use-feature-gate';

/**
 * Creates a React provider that toggles a body class based on feature gate state.
 *
 * @param featureKey - Backend feature key for plan-based gating
 * @param gateClassName - CSS class toggled on document.body when enabled
 * @returns FC that wraps children and manages the body class
 */
export function createVerticalGateProvider(
  featureKey: string,
  gateClassName: string
): FC<PropsWithChildren> {
  function VerticalGateProvider({ children }: PropsWithChildren) {
    const { isFeatureAllowed, isFeatureConfigured } = useFeatureGate();

    const isEnabled =
      isFeatureAllowed(featureKey) && isFeatureConfigured(featureKey);

    useEffect(() => {
      document.body.classList.toggle(gateClassName, isEnabled);
      return () => {
        document.body.classList.remove(gateClassName);
      };
    }, [isEnabled]);

    return <>{children}</>;
  }

  VerticalGateProvider.displayName = `VerticalGateProvider(${featureKey})`;
  return VerticalGateProvider;
}
